import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import {
  Card,
  Row,
  Col,
  Button,
  Select,
  DatePicker,
  Table,
  Tag,
  Space,
  Typography,
  Statistic,
} from "antd";
import {
  FilterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  ReloadOutlined,
  EyeOutlined,
  InboxOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import { fetchComboData } from "../../services/api";

const { Text } = Typography;
const { Option } = Select;

const numericFields = ['sno', 'id', 'opening_stock', 'stock_in', 'stock_out', 'closing_stock', 'purchase_price', 'vat', 'subtotal', 'netAmount', 'total_amount'];
const moneyFields = ['purchase_price', 'subtotal', 'netAmount', 'total_amount'];
const percentFields = ['vat'];
const dateFields = ['purchase_date'];

const formatPurchaseDate = (value) => (value ? dayjs(value).format('DD-MM-YYYY') : '-');

export default function StockReport() {
  const [filteredData, setFilteredData] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    item_id: "",
    supplier_id: "",
    fromDate: "",
    toDate: "",
  });
  const [viewingClosingStock, setViewingClosingStock] = useState(false);

  const fieldSets = {
    closing: [
      ["S.No.", "sno", "center"],
      ["Item Name", "item_name"],
      ["Unit", "unit"],
      ["Last Purchase Date", "purchase_date"],
      ["Purchase Price", "purchase_price", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Total Amount", "total_amount", "right"],
    ],
    bySupplier: [
      ["ID", "id"],
      ["Item Name", "item_name"],
      ["Supplier Name", "supplier_name"],
      ["Date of Purchase", "purchase_date"],
      ["Opening Stock", "opening_stock", "right"],
      ["Stock In", "stock_in", "right"],
      ["Stock Out", "stock_out", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Unit", "unit"],
      ["Purchase Price", "purchase_price", "right"],
      ["VAT %", "vat", "right"],
      ["Subtotal", "subtotal", "right"],
      ["Net Amount", "netAmount", "right"],
    ],
    general: [
      ["ID", "id"],
      ["Item Name", "item_name"],
      ["Supplier Name", "supplier_name"],
      ["Date of Purchase", "purchase_date"],
      ["Opening Stock", "opening_stock", "right"],
      ["Stock In", "stock_in", "right"],
      ["Stock Out", "stock_out", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Unit", "unit"],
    ],
  };

  const makeSorter = (field) => (a, b) => {
    if (numericFields.includes(field)) {
      return (parseFloat(a[field]) || 0) - (parseFloat(b[field]) || 0);
    }
    if (dateFields.includes(field)) {
      return new Date(a[field] || 0) - new Date(b[field] || 0);
    }
    return String(a[field] || '').localeCompare(String(b[field] || ''));
  };

  const renderCell = (field) => (value) => {
    let content = value;
    if (content === undefined || content === null || content === '' || (typeof content === 'number' && isNaN(content))) {
      content = '-';
    }
    if (moneyFields.includes(field)) {
      const num = parseFloat(content);
      content = `฿${(isNaN(num) ? 0 : num).toFixed(2)}`;
    }
    if (percentFields.includes(field)) {
      const num = parseFloat(content);
      content = !isNaN(num) && num > 0 ? `${num}%` : '-';
    }
    if (dateFields.includes(field)) {
      return formatPurchaseDate(value);
    }
    if (field === 'closing_stock') {
      const stockValue = parseFloat(value);
      const color = stockValue < 10 ? 'red' : stockValue < 50 ? 'orange' : 'green';
      return <Tag color={color}>{content}</Tag>;
    }
    if (field === 'item_name') {
      return <Text strong style={{ color: '#0d9488' }}>{content}</Text>;
    }
    if (field === 'netAmount' || field === 'total_amount') {
      return <Text strong style={{ color: '#059669' }}>{content}</Text>;
    }
    return content;
  };

  const fieldSet = viewingClosingStock ? fieldSets.closing : filters.supplier_id ? fieldSets.bySupplier : fieldSets.general;
  const columns = fieldSet.map(([label, field, align]) => ({
    title: label,
    dataIndex: field,
    key: field,
    align,
    sorter: makeSorter(field),
    render: renderCell(field),
  }));

  const fetchInventory = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
      if (filters.item_id) params.append('item_id', filters.item_id);
      if (filters.fromDate) params.append('start_date', filters.fromDate);
      if (filters.toDate) params.append('end_date', filters.toDate);

      const res = await axios.get(`/inventory/stock-report?${params.toString()}`, getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch stock report data");
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchInventory();
      fetchComboData("items", "id").then((res) => {
        setItems(res.filter(item => item.isstockable === '1' || item.isstockable === 1));
      });
      fetchData("suppliers", setSuppliers, "id", {});
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoApplyFilters = async (newFilters) => {
    try {
      const params = new URLSearchParams();
      if (newFilters.supplier_id) params.append('supplier_id', newFilters.supplier_id);
      if (newFilters.item_id) params.append('item_id', newFilters.item_id);
      if (newFilters.fromDate) params.append('start_date', newFilters.fromDate);
      if (newFilters.toDate) params.append('end_date', newFilters.toDate);

      const res = await axios.get(`/inventory/stock-report?${params.toString()}`, getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
      setViewingClosingStock(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to apply filters");
    }
  };

  const applyFilters = () => autoApplyFilters(filters);

  const resetFilters = async () => {
    const reset = { item_id: "", supplier_id: "", fromDate: "", toDate: "" };
    setFilters(reset);
    setViewingClosingStock(false);

    try {
      const res = await axios.get('/inventory/stock-report', getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset filters");
    }
  };

  const viewClosingStock = async () => {
    try {
      const res = await axios.get('/inventory/current-stock-summary', getHeaders());
      const stockItems = res.data?.data?.items || [];

      let final = stockItems.map((item, index) => ({
        sno: index + 1,
        item_name: item.item_name,
        unit: item.unit || 'PCS',
        purchase_date: item.last_purchase_date || null,
        purchase_price: parseFloat(item.last_purchase_price || 0),
        closing_stock: parseFloat(item.current_stock || 0),
        total_amount: parseFloat(item.stock_value || 0),
      }));

      final = final.sort((a, b) => a.item_name.localeCompare(b.item_name));
      final = final.map((item, index) => ({ ...item, sno: index + 1 }));

      setFilteredData(final);
      setViewingClosingStock(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch closing stock summary");
    }
  };

  const handlePrint = () => {
    const tableContainer = document.getElementById("tableid");
    if (!tableContainer) {
      toast.error("No data available to print.");
      return;
    }

    const table = tableContainer.querySelector("table");
    if (!table) {
      toast.error("No table found to print.");
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");
    printWindow.document.write(`
    <html>
      <head>
        <title>Stock Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; margin-bottom: 0; }
          h2 { text-align: center; margin-top: 5px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          table, th, td { border: 1px solid black; }
          th, td { padding: 8px; text-align: left; }
        </style>
      </head>
      <body>
        <h2>Stock Report</h2>
        ${table.outerHTML}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const logoImg = new Image();
    logoImg.src = "/logo.png";

    const buildTable = () => {
      doc.setFontSize(16);
      doc.text("Stock Report", 14, 20);

      const tableColumn = columns.map((col) => col.title);
      const tableRows = filteredData.map((row) =>
        columns.map((col) => dateFields.includes(col.dataIndex) ? formatPurchaseDate(row[col.dataIndex]) : (row[col.dataIndex] ?? ""))
      );

      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [13, 148, 136] },
        columnStyles: {
          [tableColumn.length - 1]: { cellWidth: 40, halign: "center" },
        },
        foot: [
          tableColumn.map((col) => {
            if (col === "Total Amount" || col === "Net Amount") {
              const field = col === "Total Amount" ? "total_amount" : "netAmount";
              const total = filteredData.reduce((acc, item) => acc + parseFloat(item[field] || 0), 0);
              return `Total: ฿${total.toFixed(2)}`;
            }
            return "";
          }),
        ],
        footStyles: { fontStyle: "bold", halign: "center" },
      });

      doc.save("stock_report.pdf");
    };

    logoImg.onload = () => {
      doc.addImage(logoImg, "PNG", 250, 10, 35, 15);
      buildTable();
    };

    logoImg.onerror = () => {
      buildTable();
    };
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.error("No records to export");
      return;
    }
    const worksheetData = filteredData.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[col.title] = dateFields.includes(col.dataIndex) ? formatPurchaseDate(row[col.dataIndex]) : (row[col.dataIndex] ?? '');
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
    XLSX.writeFile(workbook, `stock_report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const totalAmountField = viewingClosingStock ? 'total_amount' : filters.supplier_id ? 'netAmount' : null;
  const grandTotal = totalAmountField
    ? filteredData.reduce((acc, item) => acc + parseFloat(item[totalAmountField] || 0), 0)
    : 0;

  return (
    <Layout>
      <Header title="Stock Report" />
      <ToastContainer />

      <div style={{ padding: 20 }}>
        {/* Filter Card */}
        <Card
          title={
            <Space>
              <FilterOutlined style={{ color: '#0d9488' }} />
              <Text strong>Filter Stock Report</Text>
            </Space>
          }
          style={{ background: '#f0fdfa', borderColor: '#99f6e4' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Item Name</Text>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="All Items"
                value={filters.item_id || undefined}
                onChange={(value) => setFilters((prev) => {
                  const next = { ...prev, item_id: value || "" };
                  autoApplyFilters(next);
                  return next;
                })}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {items.map((item) => (
                  <Option key={item.id} value={item.id}>{item.iname}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Supplier Name</Text>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="All Suppliers"
                value={filters.supplier_id || undefined}
                onChange={(value) => setFilters((prev) => {
                  const next = { ...prev, supplier_id: value || "" };
                  autoApplyFilters(next);
                  return next;
                })}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {suppliers.map((supplier) => (
                  <Option key={supplier.id} value={supplier.id}>
                    {supplier.supplier_name || supplier.company_name || 'Unknown Supplier'}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={5}>
              <Text type="secondary" style={{ fontSize: 12 }}>From Date</Text>
              <DatePicker
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                value={filters.fromDate ? dayjs(filters.fromDate) : null}
                onChange={(date, dateString) => setFilters((prev) => {
                  const next = { ...prev, fromDate: date ? date.format('YYYY-MM-DD') : "" };
                  autoApplyFilters(next);
                  return next;
                })}
              />
            </Col>
            <Col xs={24} md={5}>
              <Text type="secondary" style={{ fontSize: 12 }}>To Date</Text>
              <DatePicker
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                value={filters.toDate ? dayjs(filters.toDate) : null}
                onChange={(date) => setFilters((prev) => {
                  const next = { ...prev, toDate: date ? date.format('YYYY-MM-DD') : "" };
                  autoApplyFilters(next);
                  return next;
                })}
              />
            </Col>
            <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button type="primary" block onClick={applyFilters}>Apply</Button>
            </Col>
          </Row>

          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Space wrap>
                <Button icon={<EyeOutlined />} onClick={viewClosingStock}>View Closing Stock</Button>
                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
                <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
                <Button icon={<FilePdfOutlined />} danger onClick={exportToPDF}>Export PDF</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportToExcel} style={{ color: "#389e0d", borderColor: "#389e0d" }}>Export Excel</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={12} md={8}>
            <Card style={{ background: '#f0fdfa' }}>
              <Statistic
                title={viewingClosingStock ? 'Closing Stock Items' : 'Total Records'}
                value={filteredData.length}
                prefix={<InboxOutlined />}
                valueStyle={{ color: '#0d9488' }}
              />
            </Card>
          </Col>
          {totalAmountField && (
            <Col xs={12} md={8}>
              <Card style={{ background: '#ecfdf5' }}>
                <Statistic
                  title={viewingClosingStock ? 'Total Stock Value' : 'Grand Total (Net Amount)'}
                  value={grandTotal}
                  precision={2}
                  prefix={<><WalletOutlined /> ฿</>}
                  valueStyle={{ color: '#059669' }}
                />
              </Card>
            </Col>
          )}
        </Row>

        {/* Data Table */}
        <Card
          style={{ marginTop: 16 }}
          title={
            <Space>
              <InboxOutlined style={{ color: '#0d9488' }} />
              <Text strong>{viewingClosingStock ? 'Closing Stock Report' : 'Inventory Stock Report'}</Text>
            </Space>
          }
        >
          <div id="tableid">
            <Table
              rowKey={(row, index) => row.id || index}
              columns={columns}
              dataSource={filteredData}
              pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} records` }}
              scroll={{ x: true }}
              locale={{ emptyText: 'No stock entries available' }}
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
}
